import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, FileDown, Loader2, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import apiClient from '../../api/client';
import { Molecule } from '../../types/chemistry';
import { getMoleculeName } from './MoleculeGrid';

interface Props {
  molecules: Molecule[];
  fileId?: string;
  onClose: () => void;
  onMoleculeClick?: (molecule: Molecule) => void;
}

interface PropsData {
  mw?: number;
  logp?: number;
  tpsa?: number;
  hbd?: number;
  hba?: number;
  rotatable_bonds?: number;
  ring_count?: number;
  fsp3?: number;
}

const COLS: {
  key: keyof PropsData;
  label: string;
  good: [number, number];
  fmt: (v: number) => string;
}[] = [
  { key: 'mw', label: 'MW (Da)', good: [150, 500], fmt: (v) => v.toFixed(1) },
  { key: 'logp', label: 'LogP', good: [-0.4, 5], fmt: (v) => v.toFixed(2) },
  { key: 'tpsa', label: 'TPSA (Å²)', good: [20, 140], fmt: (v) => v.toFixed(1) },
  { key: 'hbd', label: 'HBD', good: [0, 5], fmt: (v) => String(v) },
  { key: 'hba', label: 'HBA', good: [0, 10], fmt: (v) => String(v) },
  { key: 'rotatable_bonds', label: 'RotBonds', good: [0, 10], fmt: (v) => String(v) },
  { key: 'ring_count', label: 'Rings', good: [1, 6], fmt: (v) => String(v) },
  { key: 'fsp3', label: 'Fsp3', good: [0.25, 1], fmt: (v) => v.toFixed(3) },
];

const ragColor = (val: number, good: [number, number]) => {
  if (val >= good[0] && val <= good[1]) return '#16a34a';
  const diff = val < good[0] ? good[0] - val : val - good[1];
  const range = good[1] - good[0];
  return diff / range > 0.5 ? '#dc2626' : '#d97706';
};

const ragBg = (val: number, good: [number, number]) => {
  if (val >= good[0] && val <= good[1]) return 'rgba(22,163,106,0.06)';
  const diff = val < good[0] ? good[0] - val : val - good[1];
  const range = good[1] - good[0];
  return diff / range > 0.5 ? 'rgba(220,38,38,0.06)' : 'rgba(217,119,6,0.06)';
};

const BATCH_SIZE = 10;
const INITIAL_LIMIT = 200;

const CompareAllTable: React.FC<Props> = ({ molecules, fileId, onClose, onMoleculeClick }) => {
  const [svgs, setSvgs] = useState<Record<number, string>>({});
  const [propsData, setPropsData] = useState<Record<number, PropsData>>({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [displayLimit, setDisplayLimit] = useState(INITIAL_LIMIT);
  const [sortCol, setSortCol] = useState<keyof PropsData | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const visibleMols = useMemo(
    () => molecules.slice(0, displayLimit),
    [molecules, displayLimit],
  );

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProgress(0);
    setError(null);

    const REQUEST_TIMEOUT = 10_000; // 10s per request
    const OVERALL_TIMEOUT = 30_000; // 30s total before showing partial data

    const fetchAll = async () => {
      const svgMap: Record<number, string> = {};
      const propMap: Record<number, PropsData> = {};
      const startTime = Date.now();

      for (let i = 0; i < visibleMols.length; i += BATCH_SIZE) {
        if (cancelled) return;

        // Overall timeout — show partial data rather than spinning forever
        if (Date.now() - startTime > OVERALL_TIMEOUT) {
          if (!cancelled) {
            setError(`Loaded ${Object.keys(propMap).length}/${visibleMols.length} compounds before timeout`);
            setLoading(false);
          }
          return;
        }

        const batch = visibleMols.slice(i, i + BATCH_SIZE);

        try {
          const [svgResults, propResults] = await Promise.all([
            Promise.all(
              batch.map(async (mol) => {
                try {
                  const resp = await apiClient.post(
                    '/v2/indigo/render',
                    { structure: { value: mol.smiles, type: 'RAW' }, width: '120px', height: '80px' },
                    { responseType: 'text', timeout: REQUEST_TIMEOUT },
                  );
                  return { id: mol.id, svg: resp.data };
                } catch {
                  return { id: mol.id, svg: '' };
                }
              }),
            ),
            Promise.all(
              batch.map(async (mol) => {
                try {
                  const resp = await apiClient.post('/v2/properties/calculate', { smiles: mol.smiles }, { timeout: REQUEST_TIMEOUT });
                  return { id: mol.id, data: resp.data as PropsData };
                } catch {
                  return { id: mol.id, data: {} as PropsData };
                }
              }),
            ),
          ]);

          if (cancelled) return;
          svgResults.forEach((r) => { svgMap[r.id] = r.svg; });
          propResults.forEach((r) => { propMap[r.id] = r.data; });
          setSvgs({ ...svgMap });
          setPropsData({ ...propMap });
          setProgress(Math.min(i + BATCH_SIZE, visibleMols.length));
        } catch {
          // Batch-level error — continue with next batch
          if (cancelled) return;
          setProgress(Math.min(i + BATCH_SIZE, visibleMols.length));
        }
      }

      if (!cancelled) setLoading(false);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [visibleMols]);

  const handleSort = useCallback((col: keyof PropsData) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }, [sortCol]);

  const sortedMols = useMemo(() => {
    if (!sortCol) return visibleMols;
    return [...visibleMols].sort((a, b) => {
      const va = (propsData[a.id] as any)?.[sortCol] ?? null;
      const vb = (propsData[b.id] as any)?.[sortCol] ?? null;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [visibleMols, sortCol, sortDir, propsData]);

  const exportCSV = useCallback(() => {
    const header = ['#', 'Name', 'SMILES', ...COLS.map((c) => c.label)].join(',');
    const rows = sortedMols.map((mol, i) => {
      const name = getMoleculeName(mol.properties) || mol.smiles.slice(0, 30);
      const p = propsData[mol.id] || {};
      return [
        i + 1,
        `"${name.replace(/"/g, '""')}"`,
        `"${mol.smiles.replace(/"/g, '""')}"`,
        ...COLS.map((c) => {
          const v = (p as any)[c.key];
          return v != null ? c.fmt(v) : '';
        }),
      ].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare-all-${sortedMols.length}-compounds.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [sortedMols, propsData]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 16,
          width: '95vw',
          maxWidth: 1400,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--card-border)',
            flexShrink: 0,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--text-primary)',
                margin: 0,
              }}
            >
              Compare All — {molecules.length} Compounds
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Sortable property table with drug-likeness RAG colouring
              {loading && ` · Loading ${progress}/${visibleMols.length}...`}
              {error && ` · ${error}`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={exportCSV}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                background: 'rgba(13,148,136,0.1)',
                color: '#0D9488',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <FileDown size={13} /> Export CSV
            </button>
            <button
              onClick={() => {
                if (!fileId) return;
                window.open(
                  `/chemistry/file/${fileId}/editor?view=compareAll&popout=true`,
                  'discoverant-compare',
                  'width=1400,height=800,resizable=yes,scrollbars=yes'
                );
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: 4,
              }}
              title="Pop out to separate window"
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <ExternalLink size={16} />
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: 4,
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {loading && (
          <div style={{ height: 2, background: 'var(--card-border)', flexShrink: 0 }}>
            <div
              style={{
                height: '100%',
                width: `${(progress / visibleMols.length) * 100}%`,
                background: '#0D9488',
                transition: 'width 200ms',
              }}
            />
          </div>
        )}

        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
            }}
          >
            <thead>
              <tr
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  background: 'var(--card-bg)',
                  borderBottom: '2px solid var(--card-border)',
                }}
              >
                <th
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                    width: 40,
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                    minWidth: 120,
                  }}
                >
                  Structure
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                    minWidth: 100,
                  }}
                >
                  Name
                </th>
                {COLS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      textAlign: 'right',
                      padding: '8px 10px',
                      fontWeight: 600,
                      color: sortCol === col.key ? '#0D9488' : 'var(--text-muted)',
                      fontSize: 10,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {col.label}
                      {sortCol === col.key &&
                        (sortDir === 'asc' ? (
                          <ChevronUp size={10} />
                        ) : (
                          <ChevronDown size={10} />
                        ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedMols.map((mol, i) => {
                const name =
                  getMoleculeName(mol.properties) ||
                  mol.smiles.slice(0, 25);
                const p = propsData[mol.id];
                return (
                  <tr
                    key={mol.id}
                    onClick={() => onMoleculeClick?.(mol)}
                    style={{
                      borderBottom: '1px solid var(--card-border)',
                      cursor: onMoleculeClick ? 'pointer' : 'default',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        'var(--hover-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <td
                      style={{
                        padding: '6px 10px',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        fontSize: 10,
                      }}
                    >
                      {i + 1}
                    </td>
                    <td style={{ padding: '4px 10px' }}>
                      {svgs[mol.id] ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: svgs[mol.id],
                          }}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            maxWidth: 120,
                          }}
                        />
                      ) : loading ? (
                        <Loader2
                          size={12}
                          className="animate-spin"
                          style={{ color: 'var(--text-muted)' }}
                        />
                      ) : (
                        <span
                          style={{
                            color: 'var(--text-muted)',
                            fontSize: 10,
                          }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '6px 10px',
                        color: 'var(--text-primary)',
                        fontWeight: 500,
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={name}
                    >
                      {name}
                    </td>
                    {COLS.map((col) => {
                      const v = p
                        ? (p as any)[col.key]
                        : undefined;
                      if (v == null) {
                        return (
                          <td
                            key={col.key}
                            style={{
                              padding: '6px 10px',
                              textAlign: 'right',
                              color: 'var(--text-muted)',
                              fontFamily: 'monospace',
                            }}
                          >
                            {loading ? '...' : '—'}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col.key}
                          style={{
                            padding: '6px 10px',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            color: ragColor(v, col.good),
                            backgroundColor: ragBg(v, col.good),
                          }}
                        >
                          {col.fmt(v)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Load more */}
        {molecules.length > displayLimit && (
          <div
            style={{
              padding: '10px 20px',
              textAlign: 'center',
              borderTop: '1px solid var(--card-border)',
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => setDisplayLimit((l) => l + 200)}
              className="text-xs font-medium px-4 py-1.5 rounded-lg transition-colors"
              style={{
                background: 'rgba(13,148,136,0.1)',
                color: '#0D9488',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Load next 200 (showing {displayLimit} of{' '}
              {molecules.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareAllTable;
